/**
 * @file useAdd.ts
 */

import { useCallback } from 'react';
import { useContextValue } from '../../Context';
import dayjs from 'dayjs';

// How long the cart is kept after its last change.
export const CART_HOLD_MINUTES = 15;

const SCHEDULE_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const validateSelections = (currentSelections: any) =>
  currentSelections.filter(
    (x: any) => x && SCHEDULE_DAYS.some((day) => x[day] || x.days?.[day])
  );

export const useAdd = (callback?: (newCurrentSelections: any) => any) => {
  const { setState } = useContextValue();

  const addToSchedule = useCallback(
    (newSelections: any) => {
      setState((prevState) => {
        const validatedSelections = validateSelections(newSelections);

        // If there are no valid selections, clear currentSelections.
        if (validatedSelections.length === 0) {
          localStorage.removeItem('currentSelections');
          if (callback) {
            callback(null);
          }
          return {
            ...prevState,
            currentSelections: null,
          };
        }

        const prevCurrentSelections = prevState.currentSelections;

        if (prevCurrentSelections && prevCurrentSelections.createdAt) {
          const prevSelections = validateSelections(
            prevCurrentSelections.selections || []
          );
          const updatedSelections = [...prevSelections];

          validatedSelections.forEach((selection: any) => {
            const index = updatedSelections.findIndex(
              (existing: any) =>
                existing?.studentId === selection?.studentId &&
                existing?.program?.id === selection?.program?.id &&
                existing?.schedule?.id === selection?.schedule?.id
            );

            if (index !== -1) {
              // Update only if different
              if (
                JSON.stringify(updatedSelections[index]) !==
                JSON.stringify(selection)
              ) {
                updatedSelections[index] = selection;
              }
            } else {
              updatedSelections.push(selection);
            }
          });

          const newCurrentSelections = {
            createdAt: prevCurrentSelections.createdAt,
            // Each change restarts the timer instead of counting from the
            // first item, so a parent building a cart isn't cut off.
            validTo: dayjs().add(CART_HOLD_MINUTES, 'minutes').toDate(),
            selections: updatedSelections,
          };

          localStorage.setItem(
            'currentSelections',
            JSON.stringify(newCurrentSelections)
          );
          if (callback) {
            callback(newCurrentSelections);
          }

          return {
            ...prevState,
            currentSelections: newCurrentSelections,
          };
        } else {
          const newCurrentSelections = {
            createdAt: new Date(),
            validTo: dayjs().add(CART_HOLD_MINUTES, 'minutes').toDate(),
            selections: validatedSelections,
          };

          localStorage.setItem(
            'currentSelections',
            JSON.stringify(newCurrentSelections)
          );
          if (callback) {
            callback(newCurrentSelections);
          }

          return {
            ...prevState,
            currentSelections: newCurrentSelections,
          };
        }
      });
    },
    [setState, callback]
  );

  return addToSchedule;
};
